import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Rocket, PenTool, Package, Send, DollarSign } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Stage {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const stages: Stage[] = [
  { id: "setup", label: "Set Up", description: "Complete onboarding", icon: <Rocket className="w-4 h-4" /> },
  { id: "create", label: "Create", description: "First script written", icon: <PenTool className="w-4 h-4" /> },
  { id: "build", label: "Build", description: "First product created", icon: <Package className="w-4 h-4" /> },
  { id: "launch", label: "Launch", description: "Sales page + email funnel", icon: <Send className="w-4 h-4" /> },
  { id: "earn", label: "Earn", description: "First sale celebrated", icon: <DollarSign className="w-4 h-4" /> },
];

export function CreatorJourney() {
  const { user } = useAuth();
  const [completedStages, setCompletedStages] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStages = async () => {
      if (!user) { setLoading(false); return; }

      const completed = new Set<string>();

      // Stage 1: Setup — always complete if logged in
      completed.add("setup");

      // Stage 2: Create — has nexis projects
      const { count: scriptCount } = await supabase
        .from("nexis_projects")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      if ((scriptCount || 0) > 0) completed.add("create");

      // Stage 3: Build — has monetization projects
      const { count: productCount } = await supabase
        .from("monetization_projects")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      if ((productCount || 0) > 0) completed.add("build");

      // Stage 4: Launch — has used sales page builder or email list builder
      const { count: launchCount } = await supabase
        .from("credit_transactions")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .in("action_type", ["sales-page-builder", "email-list-builder"]);
      if ((launchCount || 0) >= 2) completed.add("launch");

      // Stage 5: Earn — has posted a product win
      const { count: winCount } = await supabase
        .from("product_wins")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      if ((winCount || 0) > 0) completed.add("earn");

      setCompletedStages(completed);
      setLoading(false);
    };

    checkStages();
  }, [user]);

  if (loading) return null;

  const currentStageIndex = stages.findIndex((s) => !completedStages.has(s.id));
  const activeIndex = currentStageIndex === -1 ? stages.length : currentStageIndex;

  return (
    <div className="glass-card p-6">
      <h2 className="font-display text-xl font-semibold mb-2">Your Creator Journey</h2>
      <p className="text-sm text-muted-foreground mb-6">
        {activeIndex >= stages.length
          ? "You've completed all stages — amazing work! 🎉"
          : `You're at Stage ${activeIndex + 1}: ${stages[activeIndex].label}`}
      </p>

      {/* Journey Progress */}
      <div className="relative">
        {/* Connection line */}
        <div className="absolute top-5 left-5 right-5 h-0.5 bg-muted hidden sm:block" />
        <div
          className="absolute top-5 left-5 h-0.5 bg-primary transition-all duration-700 hidden sm:block"
          style={{ width: `${Math.max(0, ((activeIndex - 1) / (stages.length - 1)) * 100)}%` }}
        />

        <div className="flex flex-col sm:flex-row sm:justify-between gap-4 sm:gap-0">
          {stages.map((stage, index) => {
            const isCompleted = completedStages.has(stage.id);
            const isCurrent = index === activeIndex;
            return (
              <div key={stage.id} className="flex sm:flex-col items-center sm:items-center gap-3 sm:gap-2 relative z-10">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isCompleted
                      ? "bg-green-500 text-white"
                      : isCurrent
                        ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : stage.icon}
                </div>
                <div className="sm:text-center">
                  <p className={`text-sm font-medium ${isCompleted ? "text-green-400" : isCurrent ? "text-foreground" : "text-muted-foreground"}`}>
                    {stage.label}
                  </p>
                  <p className="text-xs text-muted-foreground hidden sm:block">{stage.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
